'use client';

import { c as _c } from "react/compiler-runtime";
import type { Middleware, MPAInterceptedResponse } from '@repo/api-gw-sdk';
import { useSearchParams } from 'next/navigation';
import { useMemo, type PropsWithChildren } from 'react';
import { NonProjectDalContext } from '@/contexts/nonProjectDalContext';
import { nonProjectDalFactory } from '@/data/dal/dalFactory';
import { createHttpClient, refreshTokenIfNeeded } from '@/data/dal/httpClient';
import { createMpaMiddleware } from '@/data/dal/mpaMiddleware';
import { createConfig } from './api-config';
import type { Environment } from './environment';
import { useEnvironment } from './useEnvironment';
const createMiddleware = (authToken: string, updateAuthToken: (token: string) => void, environment: Environment, serverFlags: string | null, useRefreshToken: boolean): Middleware => {
  let refreshPromise = Promise.resolve(authToken);
  let isRefreshing = false;
  const refreshToken = () => {
    if (!useRefreshToken || isRefreshing) {
      return refreshPromise;
    }
    isRefreshing = true;
    refreshPromise = refreshTokenIfNeeded(authToken, environment.clientApiBaseUrl, updateAuthToken).then(token => {
      isRefreshing = false;
      return token;
    }, () => {
      isRefreshing = false;
      return authToken;
    });
    return refreshPromise;
  };
  return {
    pre: async context => {
      if (serverFlags) {
        context.setHeaderParam('X-Server-Flags', serverFlags);
      }
      if (!context.getUrl().endsWith('/refresh')) {
        const newAuthToken = await refreshToken();
        context.setHeaderParam('Authorization', `Bearer ${newAuthToken}`);
      }
      return context;
    },
    post: async context => {
      if (useRefreshToken && context.httpStatusCode === 401) {
        // The user is not authenticated, redirect to login
        location.href = '/logout';
      }
      return context;
    }
  };
};
interface NonProjectDalProviderProps {
  // Injected from the UI layer so the contexts module stays free of any
  // dialog/UI imports. Building the handler requires WorkspaceProvider, which
  // mounts above this provider — see `useMpaInterceptHandler`.
  mpaInterceptHandler: (payload: MPAInterceptedResponse) => Promise<void>;
  // False under the `(public)` layout (login, forgot-password, etc.). The
  // token-refresh + force-logout-on-401 behavior only makes sense for an
  // authenticated session; on public pages a 401 is a rejected credential, not
  // an expired session, so leaving this on would bounce the user to /logout
  // instead of letting the form show the error.
  isAuthenticated: boolean;
}
export const NonProjectDalProvider = t0 => {
  const $ = _c(21);
  const {
    children,
    mpaInterceptHandler,
    isAuthenticated
  } = t0;
  const env = useEnvironment();
  const searchParams = useSearchParams();
  let t1;
  if ($[0] !== searchParams) {
    t1 = searchParams.get("serverFlags");
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const serverFlags = t1;
  const useRefreshToken = isAuthenticated && (!(env.isDemo || env.isDev || env.isTest) || env.enableForceLogoutOn401);
  let config;
  if ($[2] !== env || $[3] !== mpaInterceptHandler || $[4] !== serverFlags || $[5] !== useRefreshToken) {
    config = createConfig(env.clientApiBaseUrl, env.authToken);
    config.middleware.push(createMiddleware(env.authToken, env.updateAuthToken, env, serverFlags, useRefreshToken), createMpaMiddleware(mpaInterceptHandler));
    $[2] = env;
    $[3] = mpaInterceptHandler;
    $[4] = serverFlags;
    $[5] = useRefreshToken;
    $[6] = config;
  } else {
    config = $[6];
  }
  const configuration = config;
  let t2;
  if ($[7] !== env || $[8] !== useRefreshToken) {
    t2 = createHttpClient(env.authToken, env.updateAuthToken, env, useRefreshToken);
    $[7] = env;
    $[8] = useRefreshToken;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const httpClient = t2;
  const t3 = env.isDev || env.isTest;
  let t4;
  if ($[10] !== configuration || $[11] !== httpClient || $[12] !== t3) {
    t4 = nonProjectDalFactory(httpClient, configuration, t3);
    $[10] = configuration;
    $[11] = httpClient;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const nonProjectDal = t4;
  let t5;
  if ($[14] !== configuration || $[15] !== httpClient || $[16] !== nonProjectDal) {
    t5 = {
      nonProjectDal,
      httpClient,
      configuration
    };
    $[14] = configuration;
    $[15] = httpClient;
    $[16] = nonProjectDal;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== children || $[19] !== t5) {
    t6 = <NonProjectDalContext.Provider value={t5}>{children}</NonProjectDalContext.Provider>;
    $[18] = children;
    $[19] = t5;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  return t6;
};